import type { JSX, RefObject } from 'react';
import { Children, cloneElement, useCallback, useEffect } from 'react';

export type ClickRegionProps = {
  /**
   * The content that should be clickable.
   */
  children: JSX.Element;

  /**
   * The ref to the button or link that should be clicked when the ClickRegion is clicked.
   */
  targetRef: RefObject<HTMLAnchorElement | HTMLButtonElement | null>;
};

const isClickableElement = (element: EventTarget | null): boolean => {
  if (!element) {
    return false;
  }
  const el = element as HTMLElement;
  return (
    el.closest('button') !== null ||
    el.closest('a') !== null ||
    el.closest('input') !== null ||
    el.closest('select') !== null ||
    el.closest('textarea') !== null ||
    el.closest('label') !== null ||
    el.closest('[data-wui-faux-input]') !== null
  );
};

/**
 * This allows larger elements to be interactive links or buttons while maintaining acessibility.
 *
 * For buttons, when the element is clicked, it will proxy a click event to the targetRef, which should be inside the ClickRegion.
 *
 * For links, a pseudo-element of the link is created that is the size of the ClickRegion. **Note**: this will prevent text selection
 * inside the text region.
 *
 * In either case, a link or button must be present, but can be hidden by using `ScreenReaderOnly`.
 */
export const ClickRegion = ({ children, targetRef }: ClickRegionProps): JSX.Element => {
  useEffect(() => {
    if (targetRef.current?.tagName === 'A') {
      targetRef.current.setAttribute('data-click-region-target-link', '');
    } else if (targetRef.current?.tagName === 'BUTTON') {
      targetRef.current.setAttribute('data-click-region-target-button', '');
    } else {
      //
    }
  }, [targetRef]);

  const handleClick = useCallback(
    (event: Event) => {
      const node = targetRef.current;

      // If we clicked directly on the target button, let it handle its own click
      if (event.target === node) {
        return;
      }

      // If we clicked on a button/anchor that's not the target, stop propagation
      if (isClickableElement(event.target) && event.target !== node) {
        event.stopPropagation();
        return;
      }

      if (
        node === null ||
        event.defaultPrevented ||
        !node.hasAttribute('data-click-region-target-button')
      ) {
        return;
      }

      event.stopPropagation();
      event.preventDefault();

      node.focus();
      node.click();
    },
    [targetRef],
  );

  return cloneElement(Children.only(children), {
    'data-click-region': true,
    onClick: handleClick,
  });
};

ClickRegion.displayName = 'ClickRegion';
